import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import OrderInfoTab from './OrderInfoTab';
import CaseInfoTab from './CaseInfoTab';
import CaseParticipantsTab from './CaseParticipantsTab';
import DocumentAssignmentSection from './DocumentAssignmentSection';
import CourtJurisdictionField from './CourtJurisdictionField';
import SectionConfirmControl from './SectionConfirmControl';
import { useEFiling } from '../contexts/EFilingContext';

interface ConfirmFilingTabProps {
  brand: BrandConfig;
}

/**
 * The consolidated "Confirm your filing" page (Walter R49, phase 1 structural
 * merge): Where to File + About Your Case stacked as one dependency-ordered
 * step. Flag-gated (?confirmFlow=1); the classic two-step flow is unchanged.
 *
 * Phase-1 placement rules:
 * - R64/R65 — Court Jurisdiction renders in the Court section directly
 *   beneath County (the courtJurisdictionSlot), and the existing WOL-341
 *   state/county reset is preserved; CaseInfoTab hides its inline copy.
 * - R66/R67 — the additional case options stay in CaseInfoTab's expandable
 *   block with the inline $1,000 Complex Case warning; both carry over
 *   untouched because CaseInfoTab is reused, not forked.
 *
 * Adopt-and-certify (R50–R52/R61–R63) is phase 2 — nothing here yet.
 */
const ConfirmFilingTab: React.FC<ConfirmFilingTabProps> = ({ brand }) => {
  const { ocrPopulatedFields } = useEFiling();
  return (
    <div data-testid="confirm-filing-tab">
      <OrderInfoTab
        brand={brand}
        courtJurisdictionSlot={<CourtJurisdictionField brand={brand} />}
      />
      <hr className="my-6" style={{ borderColor: brand.colors.border }} />
      <CaseInfoTab
        brand={brand}
        ocrPopulatedFields={ocrPopulatedFields}
        hideCourtJurisdiction
      />
      <hr className="my-6" style={{ borderColor: brand.colors.border }} />
      <CaseParticipantsTab brand={brand} />
      {/* SPEC-SOR-174 AX3: this step owns the parties and documents sections,
          so each one's confirm control sits directly below the content it
          approves. Both are on THIS step and nowhere else — a confirm control
          for the court or case section would put the Confirm step's Next behind
          an approval whose subject lives a step back. */}
      <SectionConfirmControl
        brand={brand}
        section="sec_parties"
        description="Check the parties and attorneys above, then confirm."
      />
      {/* Single home for ALL participant-class per-doc fields in this flow —
          Upload hides them (WOL-419 / R69/R70), so Refers-To, Party Served,
          etc. must be satisfiable here, right below the participants they
          reference. */}
      <DocumentAssignmentSection brand={brand} includeAllPartyFields />
      <SectionConfirmControl
        brand={brand}
        section="sec_documents"
        description="Check each document's type and party assignments above, then confirm."
      />
    </div>
  );
};

export default ConfirmFilingTab;
